import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2, User, Mail, Lock, Phone, MapPin, Hash, ArrowRight, Loader2, Check, MailCheck,
} from 'lucide-react';
import axios from 'axios';
import clsx from 'clsx';
import { moneyPrice } from '../utils/format';
import AuthShell, { AuthHeading, FormAlert } from '../components/auth/AuthShell';
import AuthField from '../components/auth/AuthField';

// Signing up creates a request, not a working account: the organisation verifies its email and
// then Rostery approves it (routes/organizations.js → /superadmin approve provisions the admin
// login). The copy below says exactly that.
const REVIEW_NOTE = 'After you register and verify your email, our team reviews your application and approves your account — usually within 1 business day.';

// Field order on the page — the first invalid one in this order gets focus on submit.
const ORDER = ['name', 'contact_name', 'email', 'password', 'confirm_password', 'agree'];

// Arrow keys move the choice, as in any radio group; only the chosen option is a tab stop.
function onRadioKeys(e, values, current, onChange, refs) {
  const i = Math.max(0, values.indexOf(current));
  let n = null;
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % values.length;
  else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + values.length) % values.length;
  else if (e.key === 'Home') n = 0;
  else if (e.key === 'End') n = values.length - 1;
  if (n === null) return;
  e.preventDefault();
  onChange(values[n]);
  const el = refs.current[n];
  if (el) el.focus();
}

function validateField(k, form, agree) {
  switch (k) {
    case 'name': return form.name.trim() ? null : "Enter your organisation's name.";
    case 'contact_name': return form.contact_name.trim() ? null : 'Enter the name of the main contact.';
    case 'email':
      if (!form.email.trim()) return 'Enter an email address.';
      return /\S+@\S+\.\S+/.test(form.email) ? null : 'Enter an email address like name@example.com.au.';
    case 'password':
      if (!form.password) return 'Choose a password.';
      return form.password.length >= 8 ? null : 'Use at least 8 characters.';
    case 'confirm_password':
      if (!form.confirm_password) return 'Type the password again.';
      return form.password === form.confirm_password ? null : "The passwords don't match.";
    case 'agree': return agree ? null : 'Tick the box to agree to the Terms of Service, Privacy Policy and Data Processing Addendum.';
    default: return null;
  }
}

export default function Register() {
  // The plan chosen on the public pricing page arrives as ?plan=&interval=. It is a code,
  // never a price — the server resolves the amount and the caps from its own catalogue.
  const search = new URLSearchParams(window.location.search);
  const [planCode, setPlanCode] = useState(search.get('plan') || 'starter');
  const [interval, setIntervalPref] = useState(search.get('interval') === 'year' ? 'year' : 'month');
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [chosenAddons, setChosenAddons] = useState(
    (search.get('addons') || '').split(',').map(a => a.trim()).filter(Boolean));

  useEffect(() => {
    // Sold from the same catalogue the checkout charges from, so the limits shown here are
    // the limits that will actually be enforced.
    axios.get('/api/billing/catalogue')
      .then(r => {
        const sellable = (r.data.plans || []).filter(p => !p.contact_only);
        setPlans(sellable);
        setPlanCode(c => (sellable.some(p => p.code === c) ? c : 'starter'));
        // Quantity-based capacity is not a signup decision — it is bought later, once
        // they know how many participants they actually have.
        const mods = (r.data.addons || []).filter(a => !a.per_unit);
        setAddons(mods);
        setChosenAddons(cs => cs.filter(c => mods.some(m => m.code === c)));
      })
      .catch(() => {});
  }, []);
  const [form, setForm] = useState({
    name: '', contact_name: '', email: '', password: '', confirm_password: '',
    phone: '', address: '', abn: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors]   = useState({});
  const [formErr, setFormErr] = useState(null);
  const [agree, setAgree]     = useState(false);
  const refs = useRef({});
  const planRefs = useRef([]);
  const intervalRefs = useRef([]);

  const selectedPlan = plans.find(p => p.code === planCode) || null;
  // Shown as a monthly figure in both intervals so the two are comparable at a glance —
  // to the cent, the same catalogue amounts the checkout will charge.
  const perMonth = (item) => {
    const cents = (interval === 'year' ? item.annual_cents : item.monthly_cents) || 0;
    return interval === 'year' ? cents / 12 / 100 : cents / 100;
  };
  const monthlyTotal = (() => {
    if (!selectedPlan) return 0;
    const key = interval === 'year' ? 'annual_cents' : 'monthly_cents';
    const div = interval === 'year' ? 12 : 1;
    let cents = selectedPlan[key] || 0;
    for (const code of chosenAddons) {
      const a = addons.find(x => x.code === code);
      if (a) cents += a[key] || 0;
    }
    return cents / div / 100;
  })();

  const set = (k, v) => {
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(e => ({ ...e, [k]: null }));
  };
  // Validate a field when the person leaves it (only once they have typed something), and
  // every field on submit — the same messages either way.
  const blur = (k) => () => {
    if (!form[k]) return;
    setErrors(e => ({ ...e, [k]: validateField(k, form, agree) }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormErr(null);
    const errs = {};
    for (const k of ORDER) { const m = validateField(k, form, agree); if (m) errs[k] = m; }
    setErrors(errs);
    const first = ORDER.find(k => errs[k]);
    if (first) { refs.current[first]?.focus(); return; }

    setLoading(true);
    try {
      await axios.post('/api/organizations/register', {
        name:         form.name.trim(),
        contact_name: form.contact_name.trim(),
        email:        form.email.trim(),
        password:     form.password,
        phone:        form.phone || undefined,
        address:      form.address || undefined,
        abn:          form.abn || undefined,
        plan:         planCode,
        interval,
        addons:       chosenAddons,
      });
      setSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || '';
      if (!err.response) {
        setFormErr("We couldn't reach Rostery. Check your connection, then try again.");
      } else if (status === 409) {
        setErrors(x => ({ ...x, email: 'An organisation with this email is already registered. Sign in instead, or use a different email.' }));
        refs.current.email?.focus();
      } else if (status === 400 && /password/i.test(msg)) {
        setErrors(x => ({ ...x, password: msg }));
        refs.current.password?.focus();
      } else if (status === 400 && /email/i.test(msg)) {
        setErrors(x => ({ ...x, email: msg }));
        refs.current.email?.focus();
      } else {
        setFormErr(status >= 500 || !msg ? "We couldn't create your account just now. Try again in a moment." : msg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell note={REVIEW_NOTE}>
        <div role="status">
          <span className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-5" aria-hidden="true">
            <MailCheck size={20} />
          </span>
          <AuthHeading title="Check your email">
            We've sent a verification link to <strong className="font-semibold text-slate-700">{form.email}</strong>.
            Verify your email, then our team reviews your registration and emails you when your account
            is approved — usually within 1 business day.
          </AuthHeading>
          <Link to="/login" className="btn-primary w-full py-3 text-sm">
            Back to sign in <ArrowRight size={15} aria-hidden="true" />
          </Link>
        </div>
      </AuthShell>
    );
  }

  const planCodes = plans.map(p => p.code);
  const planChecked = planCodes.includes(planCode) ? planCode : null;

  return (
    <AuthShell wide note={REVIEW_NOTE}>
      <AuthHeading title="Register your organisation">Create your Rostery account.</AuthHeading>

      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        {/* Plan — first, because it decides the limits and the modules they get */}
        {plans.length > 0 && (
          <section aria-labelledby="plan-heading" className="rounded-xl border border-slate-200 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="plan-heading" className="text-sm font-semibold text-slate-800">Choose your plan</h2>
              <div role="radiogroup" aria-label="Billing period" className="inline-flex rounded-lg p-0.5 bg-slate-100 text-[12px] font-semibold">
                {['month', 'year'].map((i, idx) => {
                  const on = interval === i;
                  return (
                    <button key={i} type="button" role="radio" aria-checked={on} tabIndex={on ? 0 : -1}
                      ref={el => { intervalRefs.current[idx] = el; }}
                      onClick={() => setIntervalPref(i)}
                      onKeyDown={e => onRadioKeys(e, ['month', 'year'], interval, setIntervalPref, intervalRefs)}
                      className={clsx('px-3 min-h-[32px] rounded-md transition-colors duration-150',
                        on ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600 hover:text-slate-900')}>
                      {i === 'month' ? 'Monthly' : 'Annual'}
                    </button>
                  );
                })}
              </div>
            </div>

            <div role="radiogroup" aria-labelledby="plan-heading" className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {plans.map((p, idx) => {
                const on = planCode === p.code;
                const tabbable = on || (!planChecked && idx === 0);
                return (
                  <button key={p.code} type="button" role="radio" aria-checked={on} tabIndex={tabbable ? 0 : -1}
                    ref={el => { planRefs.current[idx] = el; }}
                    onClick={() => setPlanCode(p.code)}
                    onKeyDown={e => onRadioKeys(e, planCodes, planCode, setPlanCode, planRefs)}
                    className={clsx('relative text-left rounded-lg border p-3 transition-colors duration-150',
                      on ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50/40' : 'border-slate-200 hover:border-slate-300')}>
                    {on && (
                      <span className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary-600 text-white flex items-center justify-center" aria-hidden="true">
                        <Check size={11} strokeWidth={3} />
                      </span>
                    )}
                    <span className="block font-semibold text-sm text-slate-900 pr-5">{p.name}</span>
                    <span className="block text-base font-semibold text-slate-900 tabular-nums leading-tight mt-0.5">
                      {moneyPrice(perMonth(p))}<span className="text-xs font-medium text-slate-500">/mo</span>
                    </span>
                    {/* These are the limits the product will actually enforce, so say
                        them here rather than letting someone find out later. */}
                    <span className="block text-xs text-slate-500 leading-snug mt-1">
                      {p.participant_limit ? `${p.participant_limit} participants` : 'Unlimited participants'}
                      <br />
                      {p.staff_limit ? `${p.staff_limit} staff` : 'Unlimited staff'}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Add-on modules. Plan management, support coordination and aged care
                are separate services with their own obligations, so they are chosen,
                not assumed — and picking one here entitles the org from day one. */}
            {addons.length > 0 && (
              <div className="pt-3 border-t border-slate-100">
                <p id="addons-heading" className="text-sm font-semibold text-slate-800 mb-2">
                  Add modules <span className="font-normal text-slate-500">(optional)</span>
                </p>
                <div role="group" aria-labelledby="addons-heading" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {addons.map(a => {
                    const on = chosenAddons.includes(a.code);
                    return (
                      <button key={a.code} type="button" role="checkbox" aria-checked={on}
                        onClick={() => setChosenAddons(cs =>
                          cs.includes(a.code) ? cs.filter(c => c !== a.code) : [...cs, a.code])}
                        className={clsx('relative text-left rounded-lg border p-3 transition-colors duration-150',
                          on ? 'border-primary-500 ring-1 ring-primary-500 bg-primary-50/40' : 'border-slate-200 hover:border-slate-300')}>
                        <span className={clsx('absolute top-2.5 right-2.5 w-4 h-4 rounded border flex items-center justify-center',
                          on ? 'bg-primary-600 border-primary-600 text-white' : 'border-slate-300 bg-white')} aria-hidden="true">
                          {on && <Check size={11} strokeWidth={3} />}
                        </span>
                        <span className="block font-semibold text-[13px] text-slate-900 leading-tight pr-6">{a.name}</span>
                        {a.subtitle && <span className="block text-xs text-slate-500 leading-snug mt-1">{a.subtitle}</span>}
                        <span className="block text-sm font-semibold text-slate-900 tabular-nums mt-1.5">
                          {moneyPrice(perMonth(a))}<span className="text-xs font-medium text-slate-500">/mo</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* What they are agreeing to, before they agree to it. */}
            {selectedPlan && (
              <div className="flex items-baseline justify-between gap-3 pt-3 border-t border-slate-100">
                <span className="text-[13px] text-slate-600">
                  {selectedPlan.name}
                  {chosenAddons.length > 0 && ` + ${chosenAddons.length} module${chosenAddons.length > 1 ? 's' : ''}`}
                </span>
                <span className="text-base font-semibold text-slate-900 tabular-nums text-right">
                  {moneyPrice(monthlyTotal)}
                  <span className="text-xs font-medium text-slate-500">
                    /mo{interval === 'year' ? ', billed annually' : ''}
                  </span>
                </span>
              </div>
            )}

            <p className="text-xs text-slate-500 leading-relaxed">
              {interval === 'year' ? 'Billed annually — two months free. ' : ''}
              Prices in AUD excluding GST. You can change plan, add modules or add
              participant capacity at any time. Nothing is charged until you enter
              payment details.
            </p>
          </section>
        )}

        {/* Organisation details */}
        <fieldset className="rounded-xl border border-slate-200 p-4">
          {/* A floated legend sits inside the box instead of notching the border. */}
          <legend className="float-left w-full mb-3 text-sm font-semibold text-slate-800">Organisation details</legend>
          <div className="clear-left space-y-4">

          <AuthField
            ref={el => { refs.current.name = el; }}
            label="Organisation name" icon={Building2}
            value={form.name} onChange={e => set('name', e.target.value)} onBlur={blur('name')}
            placeholder="Acme Disability Services" autoComplete="organization"
            error={errors.name}
          />
          <AuthField
            label="ABN" optional icon={Hash} inputMode="numeric"
            value={form.abn} onChange={e => set('abn', e.target.value)}
            placeholder="12 345 678 901"
          />
          <AuthField
            label="Address" optional icon={MapPin}
            value={form.address} onChange={e => set('address', e.target.value)}
            placeholder="123 Main St, Sydney NSW 2000" autoComplete="street-address"
          />
          </div>
        </fieldset>

        {/* Contact details */}
        <fieldset className="rounded-xl border border-slate-200 p-4">
          {/* A floated legend sits inside the box instead of notching the border. */}
          <legend className="float-left w-full mb-3 text-sm font-semibold text-slate-800">Contact details</legend>
          <div className="clear-left space-y-4">

          <AuthField
            ref={el => { refs.current.contact_name = el; }}
            label="Contact name" icon={User}
            value={form.contact_name} onChange={e => set('contact_name', e.target.value)} onBlur={blur('contact_name')}
            placeholder="Jane Smith" autoComplete="name"
            error={errors.contact_name}
          />
          <AuthField
            ref={el => { refs.current.email = el; }}
            label="Email address" icon={Mail} type="email"
            value={form.email} onChange={e => set('email', e.target.value)} onBlur={blur('email')}
            placeholder="jane@acmedisability.com.au" autoComplete="email"
            error={errors.email}
          />
          <AuthField
            label="Phone" optional icon={Phone} type="tel"
            value={form.phone} onChange={e => set('phone', e.target.value)}
            placeholder="02 9000 0000" autoComplete="tel"
          />
          </div>
        </fieldset>

        {/* Password */}
        <fieldset className="rounded-xl border border-slate-200 p-4">
          {/* A floated legend sits inside the box instead of notching the border. */}
          <legend className="float-left w-full mb-3 text-sm font-semibold text-slate-800">Set a password</legend>
          <div className="clear-left space-y-4">

          <AuthField
            ref={el => { refs.current.password = el; }}
            label="Password" icon={Lock} type="password"
            value={form.password} onChange={e => set('password', e.target.value)} onBlur={blur('password')}
            autoComplete="new-password"
            hint="At least 8 characters."
            error={errors.password}
          />
          <AuthField
            ref={el => { refs.current.confirm_password = el; }}
            label="Confirm password" icon={Lock} type="password"
            value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} onBlur={blur('confirm_password')}
            autoComplete="new-password"
            error={errors.confirm_password}
          />
          </div>
        </fieldset>

        {/* Consent */}
        <div>
          <div className="flex items-start gap-2.5">
            <input
              ref={el => { refs.current.agree = el; }}
              id="register-agree"
              type="checkbox"
              checked={agree}
              onChange={e => { setAgree(e.target.checked); setErrors(er => ({ ...er, agree: null })); }}
              aria-invalid={errors.agree ? true : undefined}
              aria-describedby={errors.agree ? 'register-agree-error' : undefined}
              className="mt-0.5 w-4 h-4 accent-primary-600 shrink-0"
            />
            <label htmlFor="register-agree" className="text-[13px] text-slate-600 leading-relaxed">
              I agree to the{' '}
              <a href="https://rostery.com.au/terms" target="_blank" rel="noreferrer" className="text-primary-700 font-medium underline underline-offset-2">Terms of Service</a>,{' '}
              <a href="https://rostery.com.au/privacy" target="_blank" rel="noreferrer" className="text-primary-700 font-medium underline underline-offset-2">Privacy Policy</a>{' '}and{' '}
              <a href="https://rostery.com.au/dpa" target="_blank" rel="noreferrer" className="text-primary-700 font-medium underline underline-offset-2">Data Processing Addendum</a>.
            </label>
          </div>
          {errors.agree && <p id="register-agree-error" role="alert" className="mt-1.5 text-xs text-red-600">{errors.agree}</p>}
        </div>

        {formErr && <FormAlert>{formErr}</FormAlert>}

        <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-sm">
          {loading
            ? <><Loader2 size={16} className="animate-spin" aria-hidden="true" /> Creating account…</>
            : <>Create account <ArrowRight size={15} aria-hidden="true" /></>}
        </button>
      </form>

      <p className="text-center text-sm text-slate-600 mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-700 font-semibold hover:text-primary-800">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
